import './broker-agencies-entity-grid.scss';

import { Box, IconButton, Link } from '@mui/material';
import {
  BrokerAgenciesEntityFieldIdType,
  BrokerAgenciesEntityFieldLabelType,
  SortOrder,
} from '@converge/data';
import {
  CircularLoader,
  DashboardHeader,
  ISortProps,
  PaginatedTable,
  PaperLayout,
  StatusBadge,
} from '../../../presentation';
import {
  EntitySubmissionType,
  IBrokerEntityInfo,
  useGetEntities,
} from '@converge/graphql';

import { ConvergeNavigationUrls } from '../../../contexts';
import { CustomPaletteColor } from '../../../theme';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { displayFormatDate } from '@converge/utils';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface BrokerAgenciesEntityDetails {
  uuid: string;
  referenceId: string;
  name?: string | null;
  brokerInfo: IBrokerEntityInfo;
  createdAt: string;
  updatedAt: string;
}

export const BrokerAgenciesEntityGrid = () => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [brokerAgenciesEntitiesSort, setBrokerAgenciesEntitiesSort] =
    useState<ISortProps>({
      id: BrokerAgenciesEntityFieldIdType.CreatedAt,
      order: SortOrder.Desc,
    });
  const { loading, data, entities } = useGetEntities({
    pageNumber: currentPage,
    pageSize: rowsPerPage,
    entityType: EntitySubmissionType.Broker,
  });

  if (loading) {
    return <CircularLoader className="circular-loader-style" />;
  }

  return (
    <>
      <Box className="entity-grid-header-title">
        <DashboardHeader
          title={t('entities.brokerAgencies.title', 'Broker agencies')}
          children={
            <div className="right-section">
              <IconButton>
                <FilterAltIcon className="filter-style" />
              </IconButton>
            </div>
          }
        />
      </Box>
      <PaperLayout className="entity-grid-layout">
        <PaginatedTable
          columns={[
            {
              id: BrokerAgenciesEntityFieldIdType.ReferenceId,
              label: BrokerAgenciesEntityFieldLabelType.ReferenceId,
              render: (row: BrokerAgenciesEntityDetails) => (
                <Box className="entity-grid-layout-reference-id">
                  <Link
                    className="reference-id"
                    href={ConvergeNavigationUrls.brokerAgencyEntityDetail({
                      brokerAgencyUuid: row.uuid,
                    })}
                  >
                    {row.referenceId}
                  </Link>
                </Box>
              ),
              isSortable: true,
            },
            {
              id: BrokerAgenciesEntityFieldIdType.Name,
              label: BrokerAgenciesEntityFieldLabelType.Name,
              render: (row: BrokerAgenciesEntityDetails) => row.name ?? 'N/A',
              isSortable: true,
            },
            {
              id: BrokerAgenciesEntityFieldIdType.BrokerLicenseNumber,
              label: BrokerAgenciesEntityFieldLabelType.BrokerLicenseNumber,
              render: (row: BrokerAgenciesEntityDetails) => {
                return (
                  <>
                    {row.brokerInfo.licenseNumber?.length > 0 ? (
                      row.brokerInfo.licenseNumber
                    ) : (
                      <Box className="status-badge">
                        <StatusBadge
                          label="Not licensed"
                          color={CustomPaletteColor.Warning}
                        />
                      </Box>
                    )}
                  </>
                );
              },
            },
            {
              id: BrokerAgenciesEntityFieldIdType.ActiveBrokersCount,
              label: BrokerAgenciesEntityFieldLabelType.ActiveBrokersCount,
              render: (row: BrokerAgenciesEntityDetails) =>
                row.brokerInfo.activeAgents,
              isSortable: true,
            },
            {
              id: BrokerAgenciesEntityFieldIdType.DefaultCommission,
              label: BrokerAgenciesEntityFieldLabelType.DefaultCommission,
              render: (row: BrokerAgenciesEntityDetails) =>
                row.brokerInfo.defaultCommission,
              isSortable: true,
            },
            {
              id: BrokerAgenciesEntityFieldIdType.AveragePremium,
              label: BrokerAgenciesEntityFieldLabelType.AveragePremium,
              render: (row: BrokerAgenciesEntityDetails) =>
                row.brokerInfo.averagePremium,
              isSortable: true,
            },
            {
              id: BrokerAgenciesEntityFieldIdType.TotalSubmissions,
              label: BrokerAgenciesEntityFieldLabelType.TotalSubmissions,
              render: (row: BrokerAgenciesEntityDetails) =>
                row.brokerInfo.totalSubmissions,
              isSortable: true,
            },
            {
              id: BrokerAgenciesEntityFieldIdType.TotalPremium,
              label: BrokerAgenciesEntityFieldLabelType.TotalPremium,
              render: (row: BrokerAgenciesEntityDetails) =>
                row.brokerInfo.totalPremium,
              isSortable: true,
            },
            {
              id: BrokerAgenciesEntityFieldIdType.CommissionEarned,
              label: BrokerAgenciesEntityFieldLabelType.CommissionEarned,
              render: (row: BrokerAgenciesEntityDetails) =>
                row.brokerInfo.commissionEarned,
              isSortable: true,
            },
            {
              id: BrokerAgenciesEntityFieldIdType.CreatedAt,
              label: BrokerAgenciesEntityFieldLabelType.CreatedAt,
              render: (row: BrokerAgenciesEntityDetails) =>
                displayFormatDate(row.createdAt),
              isSortable: true,
            },
            {
              id: BrokerAgenciesEntityFieldIdType.UpdatedAt,
              label: BrokerAgenciesEntityFieldLabelType.UpdatedAt,
              render: (row: BrokerAgenciesEntityDetails) =>
                displayFormatDate(row.updatedAt),
              isSortable: true,
            },
          ]}
          rows={entities}
          enablePagination
          rowsPerPage={rowsPerPage}
          currentPage={currentPage}
          totalRows={data?.entities?.pageInfo.totalResults || 0}
          handleChangeRowsPerPage={(newRowsPerPage) => {
            setRowsPerPage(newRowsPerPage);
            setCurrentPage(0);
          }}
          handleChangePage={setCurrentPage}
          currentSort={brokerAgenciesEntitiesSort}
          handleSort={(sort) => {
            setBrokerAgenciesEntitiesSort(sort);
            setCurrentPage(0);
          }}
          emptyRowMessage={t(
            'brokerAgencies.grid.emptyMessage',
            'No broker agencies found'
          )}
        />
      </PaperLayout>
    </>
  );
};
